import type { FigmaTemplateEnforcementContract } from '@/features/cogo-xyz/figma-template-ontology-matching'

export interface TemplateGenerationModelPolicy {
  schemaVersion: 'cogo.template-generation-model-policy.v1'
  provider: 'qwen'
  role: 'bounded-template-content-and-binding-generator'
  allowedOutputKinds: readonly ['content', 'bindings']
  finalWriter: 'deterministic-cogo-compiler'
  llmMayChooseTemplate: false
  llmMayOverrideLayout: false
  llmMayOverrideStyle: false
  llmMayPromoteFallback: false
  mayPatchCogoJsonDirectly: false
}

export interface BuildTemplateGenerationModelPolicyInput {
  templateEnforcement?: FigmaTemplateEnforcementContract
  suppliedPolicy?: unknown
}

const CANONICAL_POLICY: TemplateGenerationModelPolicy = Object.freeze({
  schemaVersion: 'cogo.template-generation-model-policy.v1',
  provider: 'qwen',
  role: 'bounded-template-content-and-binding-generator',
  allowedOutputKinds: Object.freeze(['content', 'bindings'] as const),
  finalWriter: 'deterministic-cogo-compiler',
  llmMayChooseTemplate: false,
  llmMayOverrideLayout: false,
  llmMayOverrideStyle: false,
  llmMayPromoteFallback: false,
  mayPatchCogoJsonDirectly: false,
})

const LOCKED_POLICY_FIELDS = [
  'llmMayChooseTemplate',
  'llmMayOverrideLayout',
  'llmMayOverrideStyle',
  'llmMayPromoteFallback',
  'mayPatchCogoJsonDirectly',
] as const

function readRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

function assertTemplateEnforcementLocks(
  templateEnforcement: FigmaTemplateEnforcementContract,
): void {
  const enforcement = readRecord(templateEnforcement)
  const authority = readRecord(enforcement?.authority)
  if (!enforcement || !authority) {
    throw new Error(
      'Template generation authority is invalid: a supplied template enforcement contract must include authority locks.',
    )
  }

  for (const field of LOCKED_POLICY_FIELDS.slice(0, 4)) {
    if (authority[field] !== false) {
      throw new Error(
        `Template generation authority is locked: ${field} must be false.`,
      )
    }
  }
}

export function assertTemplateGenerationModelPolicy(
  value: unknown,
  templateEnforcement?: FigmaTemplateEnforcementContract,
): asserts value is TemplateGenerationModelPolicy {
  if (templateEnforcement) {
    assertTemplateEnforcementLocks(templateEnforcement)
  }

  const policy = readRecord(value)
  if (!policy) {
    throw new Error('Template generation model policy is required.')
  }

  if (
    policy.schemaVersion !== CANONICAL_POLICY.schemaVersion ||
    policy.provider !== CANONICAL_POLICY.provider ||
    policy.role !== CANONICAL_POLICY.role ||
    policy.finalWriter !== CANONICAL_POLICY.finalWriter
  ) {
    throw new Error(
      'Template generation model policy must use Qwen as a bounded proposer and deterministic-cogo-compiler as the final writer.',
    )
  }

  const allowedOutputKinds = policy.allowedOutputKinds
  if (
    !Array.isArray(allowedOutputKinds) ||
    allowedOutputKinds.length !== 2 ||
    allowedOutputKinds[0] !== 'content' ||
    allowedOutputKinds[1] !== 'bindings'
  ) {
    throw new Error(
      'Template generation model policy may emit bounded content and bindings only.',
    )
  }

  for (const field of LOCKED_POLICY_FIELDS) {
    if (policy[field] !== false) {
      throw new Error(
        `Template generation model policy is locked: ${field} must be false.`,
      )
    }
  }
}

export function buildTemplateGenerationModelPolicy(
  input: BuildTemplateGenerationModelPolicyInput = {},
): TemplateGenerationModelPolicy {
  if (input.suppliedPolicy !== undefined && !input.templateEnforcement) {
    throw new Error(
      'Template generation model policy requires a template enforcement contract.',
    )
  }
  const policy = input.suppliedPolicy ?? CANONICAL_POLICY
  assertTemplateGenerationModelPolicy(policy, input.templateEnforcement)
  return CANONICAL_POLICY
}
